import React, { useState } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  IconAdd,
  LayoutSingleColumn,
  BlockUserButton,
  ContactBlockedNotice,
  useBlockStatus,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;

/**
 * Render hints for the listing card images. These follow the grid in
 * ProfilePage.module.css: one column below 460px, two up to 768px, and three
 * above that inside a content column that stops growing at 1080px.
 */
const LISTING_RENDER_SIZES = [
  '(max-width: 459px) 100vw',
  '(max-width: 767px) 50vw',
  '328px',
].join(', ');

// Mean of the ratings the user has been given, or null when there are none.
const averageRating = reviews => {
  if (!reviews?.length) return null;
  const sum = reviews.reduce((acc, r) => acc + (r?.attributes?.rating || 0), 0);
  return sum / reviews.length;
};

// The design addresses the user by first name ("Anna's listings"), while the
// heading above it uses the full display name ("Anna G.").
const firstNameOf = displayName =>
  typeof displayName === 'string' ? displayName.trim().split(/\s+/)[0] : null;

/**
 * The green-mist hero band above the fold: avatar, name, a row of profile
 * stats, and — on your own profile — the "Edit profile" / "New listing"
 * actions.
 *
 * The design's stat row has four entries. Two of them have no data source on a
 * public profile in this marketplace and are left out: the seller's location
 * (users have no location field) and the number of items sold (transactions of
 * another user are not readable).
 *
 * On someone else's profile, a signed-in visitor gets "Block user" / "Unblock user"
 * instead (P25028-272), and a neutral notice when the two cannot contact each other.
 */
export const ProfileHero = props => {
  const { user, displayName, isCurrentUser, listingCount, reviews, intl, blockStatus } = props;
  const { isBlocked, blockedByMe, canBlock } = blockStatus || {};

  const createdAt = ensureUser(user).attributes?.createdAt;
  const memberSince =
    createdAt instanceof Date
      ? intl.formatDate(createdAt, { year: 'numeric', month: 'short' })
      : null;

  const rating = averageRating(reviews);

  return (
    <section className={css.hero}>
      <div className={css.heroInner}>
        <AvatarLarge
          className={css.avatar}
          initialsClassName={css.avatarInitials}
          user={user}
          disableProfileLink
        />

        <div className={css.heroText}>
          <H2 as="h1" rootClassName={css.heroHeading}>
            {isCurrentUser ? (
              <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
            ) : displayName ? (
              <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
            ) : null}
          </H2>

          <div className={css.heroStats}>
            {memberSince ? (
              <span className={css.heroStat}>
                <FormattedMessage id="ProfilePage.memberSince" values={{ date: memberSince }} />
              </span>
            ) : null}
            {listingCount > 0 ? (
              <span className={css.heroStat}>
                <FormattedMessage
                  id="ProfilePage.listingCount"
                  values={{ count: listingCount }}
                />
              </span>
            ) : null}
            {rating != null ? (
              <span className={classNames(css.heroStat, css.heroRating)}>
                <svg
                  className={css.heroRatingStar}
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  focusable="false"
                >
                  <polygon points="12 2 15 8.5 22 9.5 17 14.5 18.5 22 12 18.5 5.5 22 7 14.5 2 9.5 9 8.5" />
                </svg>
                <FormattedMessage
                  id="ProfilePage.ratingSummary"
                  values={{ rating: rating.toFixed(1), count: reviews.length }}
                />
              </span>
            ) : null}
          </div>
          {isBlocked ? <ContactBlockedNotice className={css.heroBlockedNotice} /> : null}
        </div>

        {isCurrentUser ? (
          <div className={css.heroActions}>
            {/* Both of the pre-refresh edit links are kept: the short "Edit"
                label is used where a full-width row would not fit. */}
            <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
              <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
            </NamedLink>
            <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
              <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
            </NamedLink>
            <NamedLink className={css.newListingLink} name="NewListingPage">
              <IconAdd className={css.newListingPlus} />
              <FormattedMessage id="ProfilePage.newListingLink" />
            </NamedLink>
          </div>
        ) : canBlock ? (
          <div className={css.heroActions}>
            <BlockUserButton otherUser={user} blockedByMe={blockedByMe} />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const desktopReviewTabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeProviderSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeCustomerSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const MainContent = props => {
  const {
    userShowError,
    bio,
    displayName,
    isCurrentUser,
    listings,
    queryListingsError,
    reviews,
    queryReviewsError,
    viewport,
  } = props;

  const hasListings = listings.length > 0;
  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const hasBio = !!bio;

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div className={css.content}>
      {hasBio ? <p className={css.bio}>{bio}</p> : null}
      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" rootClassName={css.listingsTitle}>
            {isCurrentUser ? (
              <FormattedMessage
                id="ProfilePage.listingsTitle"
                values={{ count: listings.length }}
              />
            ) : (
              <FormattedMessage
                id="ProfilePage.listingsTitleOther"
                values={{ name: firstNameOf(displayName), count: listings.length }}
              />
            )}
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard
                  listing={l}
                  showAuthorInfo={false}
                  renderSizes={LISTING_RENDER_SIZES}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {isMobileLayout ? (
        <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      ) : (
        <DesktopReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      )}
    </div>
  );
};

const ProfilePageComponent = props => {
  const config = useConfiguration();
  const { scrollingDisabled, currentUser, userShowError, user, intl, ...rest } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName } = profileUser?.attributes?.profile || {};
  const blockStatus = useBlockStatus({ currentUser, otherUserId: profileUser.id?.uuid });

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.pageBackground}
      >
        <ProfileHero
          user={user}
          displayName={displayName}
          isCurrentUser={isCurrentUser}
          listingCount={rest.listings?.length || 0}
          reviews={rest.reviews || []}
          intl={intl}
          blockStatus={blockStatus}
        />
        <div className={css.divider} />
        <MainContent
          bio={bio}
          displayName={displayName}
          isCurrentUser={isCurrentUser}
          userShowError={userShowError}
          {...rest}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
